import { AfterViewInit, Directive, Inject, InjectionToken, Optional, Provider, Self, Type } from '@angular/core';
import { DateInputFormatPlaceholder, TimePickerComponent } from '@progress/kendo-angular-dateinputs';
import { QPointTimePickerUnitDirective } from './time-picker-unit';
import { injectDestroyRef } from '@qpoint/utils';

export interface QPointTimePickerConfigServiceImplementation {
  getConfig: () => DateInputFormatPlaceholder;
}

export const DI_TIME_PICKER_OVERRIDE_ACTIVE = new InjectionToken<boolean>('Boolean to enable/disable override directive');
export function provideTimePickerOverrideActive(active: boolean): Provider {
  return {
    provide: DI_TIME_PICKER_OVERRIDE_ACTIVE,
    useValue: active
  };
}

export const DI_TIME_PICKER_FORMAT_PLACEHOLDER = new InjectionToken<QPointTimePickerConfigServiceImplementation>('Format placeholder for kendo time picker');
export function provideTimePickerFormatPlaceholder(useClass: Type<QPointTimePickerConfigServiceImplementation>): Provider {
  return {
    provide: DI_TIME_PICKER_FORMAT_PLACEHOLDER,
    useClass
  };
}

@Directive({
  selector: 'kendo-timepicker, [qpointTimePickerOverride]',
  standalone: true,
})
export class QPointTimePickerOverrideDirective implements AfterViewInit {
  private destroyRef = injectDestroyRef();
  private observer: MutationObserver;

  constructor(
    @Optional() @Inject(DI_TIME_PICKER_OVERRIDE_ACTIVE) private overrideActive: boolean = true,
    @Optional() @Inject(DI_TIME_PICKER_FORMAT_PLACEHOLDER) private formatPlaceholder: QPointTimePickerConfigServiceImplementation,
    @Self() @Optional() private timepickerUnit: QPointTimePickerUnitDirective,
    @Self() public kendoTimepicker: TimePickerComponent) {
    if (this.kendoTimepicker && this.overrideActive == null || this.overrideActive === true) {
      kendoTimepicker.formatPlaceholder = this.formatPlaceholder?.getConfig() || {hour: 'hh', minute: 'mm'} as DateInputFormatPlaceholder;
    }

    this.observer = new MutationObserver(() => this.checkValue());

    this.destroyRef.onDestroy(() => {
      this.observer.disconnect();
    })
  }

  private get inputElement(): HTMLInputElement | undefined {
    return this?.kendoTimepicker?.dateInput?.inputElement;
  }

  ngAfterViewInit() {
    if (this.kendoTimepicker && this.inputElement && this.overrideActive == null || this.overrideActive === true) {
      this.checkValue();

      this.observer.observe(this.inputElement, {attributes: true})
    }
  }

  private checkValue() {
    const wrapperEl = this.kendoTimepicker.wrapper.nativeElement as HTMLElement;
    const inputValue = this.kendoTimepicker.dateInput.inputValue;
    if (inputValue.split(':').map((item) => item.replace(this?.timepickerUnit?.text, '').trim()).some(entry => !isNaN(+entry))) {
      wrapperEl.classList.remove('default-placeholder-color');
    } else {
      wrapperEl.classList.add('default-placeholder-color');
    }
  }
}
